import * as React from 'react';
import { FormattedNumber } from 'react-intl';

import { CatalogVariantFragmentFragment } from '@notino/shared/definitions/types';

import { ILocale } from '@containers/App/model';
import { useIsDualPrice } from '@containers/ProductDetailContainer/ProductDetail/hooks/useIsDualPrice';
import { usePriceLogic } from '@containers/ProductDetailContainer/ProductDetail/hooks/usePriceLogic';
import { getDiscountPercentage } from '@containers/ProductDetailContainer/ProductDetail/hooks/usePriceLogic/utils';

import { Discount, DiscountWrapper, LineThrough, RrpSlash } from './styled';

interface IVariantDiscountProps {
  variant: CatalogVariantFragmentFragment;
  locale: ILocale;
}

export const VariantDiscount: React.FC<IVariantDiscountProps> = ({
  variant,
  locale,
}) => {
  const { topPrice } = usePriceLogic(variant);
  const {
    isDualPrice,
    rightSideCurrencyCode,
    leftSideCurrency,
    getRightSidePrice,
    getLeftSideFormattedPrice,
  } = useIsDualPrice();

  if (!topPrice) {
    return null;
  }

  return (
    <DiscountWrapper>
      <Discount>
        {-getDiscountPercentage(variant.price.value ?? 0, topPrice.value.value)}
        %
      </Discount>

      {isDualPrice && (
        <>
          <LineThrough
            active={topPrice.isCrossed}
            data-testid="originalDualPriceLineThroughWrapper"
          >
            {getLeftSideFormattedPrice(topPrice.value.value)} {leftSideCurrency}
          </LineThrough>
          <RrpSlash>/</RrpSlash>
        </>
      )}

      <LineThrough
        removeLeftPadding={isDualPrice}
        active={topPrice.isCrossed}
        data-has-line-through={topPrice.isCrossed}
        data-testid="originalPriceLineThroughWrapper"
      >
        <FormattedNumber
          value={
            isDualPrice
              ? getRightSidePrice(topPrice.value.value)
              : topPrice.value.value
          }
          currency={
            isDualPrice ? rightSideCurrencyCode : topPrice.value.currency
          }
          currencyDisplay="symbol"
          style="currency"
          minimumFractionDigits={locale.priceDecimalPlaces}
        />
      </LineThrough>
    </DiscountWrapper>
  );
};
